import { useMutation } from '@tanstack/react-query';
import { splitEvery } from 'ramda';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Author } from 'apis/restaurant-api/merchant-admin/menu2/types';
import useCurrentUser from 'common/hooks/useCurrentUser';
import { getErrorContent } from 'common/utils';
import { offeringApiBatchUpdateOverrides } from 'modules/shared-menu/api';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import {
  OfferOverridesEntry,
  OfferingApiBatchUpdateOverridesPayload,
  OfferingApiError,
} from 'modules/shared-menu/types';

type ExecuteBatchUpdateOfferOverridesArgs = {
  overridesPayloads: OfferingApiBatchUpdateOverridesPayload['offer_overrides_entries'];
  selectionId: string;
  onSuccess: (successfullyUpdatedOffers: OfferOverridesEntry[]) => void;
  onError: (updateErrors: OfferingApiError[]) => void;
};

const OFFER_CHUNK_SIZE = 100;

export const useBatchUpdateOfferOverrides = () => {
  const scope = useScopeContext();
  const intl = useIntl();
  const { data: user } = useCurrentUser();
  const author: Author = useMemo(() => ({ kind: 'user', id: user?._id.$oid ?? '' }), [user?._id.$oid]);

  const { mutateAsync, isLoading } = useMutation(async (payload: OfferingApiBatchUpdateOverridesPayload) =>
    offeringApiBatchUpdateOverrides(payload),
  );

  const executeBatchUpdateOfferOverrides = useCallback(
    async ({ overridesPayloads, selectionId, onSuccess, onError }: ExecuteBatchUpdateOfferOverridesArgs) => {
      const offerOverridesEntries = splitEvery(OFFER_CHUNK_SIZE, overridesPayloads);
      const successfullyUpdatedOffers: OfferOverridesEntry[] = [];
      const updateErrors: OfferingApiError[] = [];

      for (const overridesChunk of offerOverridesEntries) {
        const payload: OfferingApiBatchUpdateOverridesPayload = {
          selection_id: selectionId,
          offer_overrides_entries: overridesChunk,
          request_scope: scope,
          author,
        };

        try {
          const response = await mutateAsync(payload);
          successfullyUpdatedOffers.push(...response.offer_overrides_entries);
        } catch (e) {
          const offerErrors: OfferingApiError[] = overridesChunk.map(({ offer_id: id }) => ({
            id,
            code: '',
            message: getErrorContent(e) || intl.formatMessage({ id: 'errors.unknown-error-occurred' }),
          }));
          updateErrors.push(...offerErrors);
        }
      }

      if (successfullyUpdatedOffers.length) {
        onSuccess(successfullyUpdatedOffers);
      }

      if (updateErrors.length) {
        onError(updateErrors);
      }
    },
    [author, intl, mutateAsync, scope],
  );

  return { executeBatchUpdateOfferOverrides, isLoading };
};
